import { Button } from '../ui/Button';
import Image from 'next/image';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import AnimatedSection from '../ui/AnimatedSection';

interface WhyUsProps {
  className?: string;
}

const WhyUs = ({ className }: WhyUsProps) => {
  return (
    <section
      className={cn(
        'relative bg-gradient-to-b from-stone-50/20 to-white overflow-hidden',
        className
      )}
    >
      {/* Ambient Red Spotlight Glows */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-bl from-primary/10 to-transparent rounded-bl-full opacity-50 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-primary/10 to-transparent rounded-tr-full opacity-50 pointer-events-none" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-radial from-primary/5 to-transparent rounded-full opacity-30 pointer-events-none" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-20 lg:py-28">
        {/* Section Header */}
        <AnimatedSection animation="fadeIn">
          <div className="text-center max-w-3xl mx-auto mb-14 lg:mb-16">
            <div className="inline-flex items-center rounded-full bg-red-50 border border-red-100 px-4 py-1.5 mb-5 text-sm font-medium text-red-800">
              <span className="flex h-2 w-2 rounded-full bg-red-500 mr-2 animate-pulse" />
              Neden Bizi Tercih Etmelisiniz?
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 tracking-tight">
              <span className="text-red-800">Kalite</span> ve{' '}
              <span className="text-red-800">Güvenin</span> Adresi
            </h2>
            <p className="text-gray-500 text-lg mt-4 leading-relaxed">
              Filiz Dekorasyon olarak, salonunuzun ruhunu yansıtan tasarımlar üretiyoruz. Estetik çizgimizi, 
              doğrudan üretici olmanın sağladığı yüksek kalite standartları ve ödün vermediğimiz güven ilkemizle birleştiriyoruz.
            </p>
          </div>
        </AnimatedSection>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-5 max-w-5xl mx-auto">
          {/* Card 1 — 20+ Yıllık Deneyim (spans 2 cols) */}
          <AnimatedSection animation="slideUp" delay={100}>
            <div className="relative group bg-white rounded-3xl shadow-sm border border-gray-100 p-8 sm:p-10 md:col-span-1 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 h-full">
              {/* Gradient accent */}
              <div className="absolute -top-16 -left-16 w-40 h-40 bg-gradient-to-br from-red-500/10 to-transparent rounded-full pointer-events-none" />

              <div className="relative z-10">
                {/* Icon */}
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center mb-6 shadow-lg shadow-red-500/20">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>

                {/* Counter */}
                <div className="mb-3">
                  <span className="text-6xl sm:text-7xl font-extrabold bg-gradient-to-r from-red-700 via-red-600 to-red-500 bg-clip-text text-transparent leading-none tracking-tight">
                    20+
                  </span>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Köklü Üretim Tecrübesi
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Sektörde çeyrek asra yaklaşan deneyimimiz ve binlerce başarılı salon kurulumumuzla mobilyada ustalığı temsil ediyoruz.
                </p>
              </div>
            </div>
          </AnimatedSection>

          {/* Card 2 — Kaliteli Malzeme */}
          <AnimatedSection animation="slideUp" delay={200}>
            <div className="relative group bg-white rounded-3xl shadow-sm border border-gray-100 p-8 sm:p-10 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 h-full">
              {/* Gradient accent */}
              <div className="absolute -top-16 -left-16 w-40 h-40 bg-gradient-to-br from-red-500/8 to-transparent rounded-full pointer-events-none" />

              <div className="relative z-10">
                {/* Icon */}
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center mb-6 shadow-lg shadow-red-500/20">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Birinci Sınıf Malzeme
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Paslanmaz çelik aksamlar, suya dayanıklı tezgahlar ve aşınmaya karşı dirençli özel deri döşemeler ile uzun ömürlü kullanımı garanti ediyoruz.
                </p>

                {/* Decorative dots */}
                <div className="mt-6 flex gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="w-2 h-2 rounded-full bg-red-400" />
                  <span className="w-2 h-2 rounded-full bg-red-300" />
                  <span className="w-2 h-2 rounded-full bg-red-200" />
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Card 3 — Profesyonel Ekip */}
          <AnimatedSection animation="slideUp" delay={300}>
            <div className="relative group bg-white rounded-3xl shadow-sm border border-gray-100 p-8 sm:p-10 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 h-full">
              {/* Gradient accent */}
              <div className="absolute -top-16 -left-16 w-40 h-40 bg-gradient-to-br from-red-500/8 to-transparent rounded-full pointer-events-none" />

              <div className="relative z-10">
                {/* Icon */}
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center mb-6 shadow-lg shadow-red-500/20">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Uzman Tasarım & Montaj
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  Kendi bünyemizdeki profesyonel tasarım ve montaj ekiplerimizle alanınızı en verimli ve estetik şekilde planlıyor, sıfır hata ile kuruyoruz.
                </p>

                {/* Decorative dots */}
                <div className="mt-6 flex gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                  <span className="w-2 h-2 rounded-full bg-red-400" />
                  <span className="w-2 h-2 rounded-full bg-red-300" />
                  <span className="w-2 h-2 rounded-full bg-red-200" />
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* Card 4 — Profesyonel Çözüm Ortağınız (spans 2 cols) */}
          <AnimatedSection animation="slideUp" delay={400}>
            <div className="relative group bg-white rounded-3xl shadow-sm border border-gray-100 p-8 sm:p-10 md:col-span-1 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 h-full">
              {/* Gradient accent */}
              <div className="absolute -top-20 -right-20 w-52 h-52 bg-gradient-to-bl from-red-500/8 to-transparent rounded-full pointer-events-none" />
              <div className="absolute -bottom-16 -left-16 w-40 h-40 bg-gradient-to-tr from-red-500/5 to-transparent rounded-full pointer-events-none" />

              <div className="relative z-10">
                {/* Icon */}
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 flex items-center justify-center mb-6 shadow-lg shadow-red-500/20">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Aracısız Üretici Güvencesi
                </h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-6">
                  Doğrudan imalattan satış modelimiz sayesinde aracı komisyonlarını ortadan kaldırıyor, en lüks salon tasarımlarına en rekabetçi fiyatlarla ve esnek sipariş imkanlarıyla ulaşmanızı sağlıyoruz.
                </p>

                {/* Features list */}
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-600 text-sm">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-50 flex items-center justify-center mr-3">
                      <svg
                        className="w-3 h-3 text-red-800"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                    Özel tasarım mobilyalar
                  </li>
                  <li className="flex items-center text-gray-600 text-sm">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-50 flex items-center justify-center mr-3">
                      <svg
                        className="w-3 h-3 text-red-800"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                    Profesyonel kurulum hizmeti
                  </li>
                  <li className="flex items-center text-gray-600 text-sm">
                    <span className="flex-shrink-0 w-5 h-5 rounded-full bg-red-50 flex items-center justify-center mr-3">
                      <svg
                        className="w-3 h-3 text-red-800"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="3"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </span>
                    Uzun yıllar garanti
                  </li>
                </ul>

                {/* CTA */}
                <Link href="/hakkimizda">
                  <Button
                    size="lg"
                    className="bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800 shadow-lg shadow-red-500/20 rounded-xl px-8 transition-all duration-300 hover:shadow-xl hover:shadow-red-500/30"
                  >
                    Daha Fazla Bilgi
                    <svg
                      className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M17 8l4 4m0 0l-4 4m4-4H3"
                      />
                    </svg>
                  </Button>
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default WhyUs;